import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Stack,
    Alert
} from '@mui/material'
import {
    Event as EventIcon,
    CloudDownload as DownloadIcon,
    CalendarToday as CalendarIcon
} from '@mui/icons-material'
import { toast } from 'react-hot-toast'
import { useLoading } from '../../hooks'
import { formatDate } from '../../utils/formatters'
import { holidayApi } from '../../api/holidayApi'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

const HolidayList = () => {
    const { loading, startLoading, stopLoading } = useLoading()
    const [holidays, setHolidays] = useState([])

    const fetchHolidays = async () => {
        startLoading()
        try {
            const response = await holidayApi.getHolidays()
            // Map the API response holiday_type to the internal type field
            const formattedHolidays = response.data.map(h => ({
                ...h,
                type: h.holiday_type
            }))
            setHolidays(formattedHolidays)
        } catch (error) {
            console.error('Error fetching holidays:', error)
            toast.error('Failed to load holiday list')
        } finally {
            stopLoading()
        }
    }

    useEffect(() => {
        fetchHolidays()
    }, [])



    const handleDownload = async () => {
        try {
            // Generate CSV download
            const headers = ['Date,Name,Type\n']
            const csvContent = holidays.map(h => `${h.date},${h.name},${h.type}`).join('\n')
            const blob = new Blob([headers + csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `Holidays_${new Date().getFullYear()}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            toast.success('Download started')
        } catch (error) {
            console.error('Error downloading holidays:', error)
            toast.error('Failed to download holiday list')
        }
    }

    return (
        <Box sx={{ p: { xs: 1, md: 3 } }}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                        Public Holidays {new Date().getFullYear()}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Download List
                    </Button>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Below is the list of official holidays for the current year. Please plan your leaves accordingly.
                </Alert>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: 'background.default' }}>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Holiday Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Day</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableSkeleton columns={4} rows={5} />
                                ) : holidays.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                            <Typography color="textSecondary">
                                                No holidays available
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    holidays.map((holiday) => {
                                        return (
                                            <TableRow key={holiday.id} hover>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <EventIcon color="primary" fontSize="small" />
                                                        <Typography fontWeight="medium">
                                                            {formatDate(holiday.date, 'MMM d')}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography fontWeight="medium">{holiday.name}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={holiday.type === 'mandatory' ? 'Mandatory' : 'Optional'}
                                                        size="small"
                                                        color={holiday.type === 'mandatory' ? 'error' : 'info'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {formatDate(holiday.date, 'EEEE')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Paper>
        </Box>
    )
}

export default HolidayList
